// Validate and (if needed) downscale an image in the browser before uploading,
// so 20–30 MB phone photos become small, model-ready files. Returns the
// original file when it's already small and in-bounds. The server re-validates
// and right-sizes authoritatively (app/utils/image_fit.py) — this is purely for
// a fast, seamless upload and instant feedback.

export class ImagePrepError extends Error {}

const MAX_SIDE = 2048; // longest side cap — well under Stability's 9.4 MP limit
const MAX_PIXELS = 8_800_000; // safety margin under 9,437,184
const MIN_SIDE = 64;
const AR_LIMIT = 2.5; // Stability allows 1:2.5 .. 2.5:1
const HEAVY_BYTES = 6 * 1024 * 1024; // recompress even if dimensions are fine

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new ImagePrepError("Could not read this image file."));
    img.src = url;
  });
}

export async function prepareImage(file: File): Promise<File> {
  if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
    throw new ImagePrepError("Use a PNG, JPG or WebP image.");
  }

  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const w = img.naturalWidth;
    const h = img.naturalHeight;

    if (w < MIN_SIDE || h < MIN_SIDE) {
      throw new ImagePrepError(`Image is too small — at least ${MIN_SIDE}px on each side.`);
    }
    const ar = w / h;
    if (ar > AR_LIMIT || ar < 1 / AR_LIMIT) {
      throw new ImagePrepError(
        "Image is too wide or tall (ratio must be between 1:2.5 and 2.5:1). Crop it and try again."
      );
    }

    const scale = Math.min(1, MAX_SIDE / Math.max(w, h), Math.sqrt(MAX_PIXELS / (w * h)));
    if (scale >= 1 && file.size <= HEAVY_BYTES) return file; // already fine

    const tw = Math.max(MIN_SIDE, Math.round(w * scale));
    const th = Math.max(MIN_SIDE, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file; // fall back to original; server will right-size
    ctx.drawImage(img, 0, 0, tw, th);

    const isPng = file.type.toLowerCase() === "image/png";
    const outType = isPng ? "image/png" : "image/jpeg";
    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob(res, outType, 0.9)
    );
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, isPng ? ".png" : ".jpg");
    return new File([blob], name, { type: outType });
  } finally {
    URL.revokeObjectURL(url);
  }
}
